import React from "react";
import axios from "axios";
import { toast } from "react-toastify";
import LogoutIcon from "@mui/icons-material/Logout";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import { getRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_now_tasks_kiosk,
  API_get_plan_tasks_for_date,
  API_get_store_team_members,
  API_save_plan_task_assignment_kiosk,
} from "../../../constants";
import LoadSpinner from "../../../utilities/LoadSpinner";
import NowTable from "../plan/tasks_hub/NowTable";
import { DayGanttMap, entriesForPerson } from "./DayGanttMap";
import TaskNameCardEmployeeModal from "../plan/plan_contents/TaskNameCardEmployeeModal";
import { mergeNowRowOverOccurrence } from "../plan/tasks_hub/taskHubUtils";
import KioskIdentityGate from "./KioskIdentityGate";

// The kiosk Tasks tab, one person at a time. They identify at the top (name
// or PIN, per the store flag), then see THEIR Now list, the same table and
// checklist modal as the employee page, and work through it. Every request
// carries their identity and goes through the *_kiosk twins on the manager
// token that entered kiosk mode. Done, or two idle minutes, returns to the
// identity screen; a running work session is left to the server's own idle
// rule, so the person can come back and pick up where they were.

export const KIOSK_IDLE_MS = 2 * 60 * 1000;
const POLL_MS = 60 * 1000;

function errorMessage(error, fallback) {
  return error?.response?.data?.system?.message || error?.response?.data?.message || fallback;
}

export default function KioskTasksPanel({
  storeId,
  homeCompany,
  roleNames,
  planTasks,
  pinRequired,
  onChanged,
  // The store's day strip ({entries, axisStart, axisEnd, nowQh}), drawn under
  // the signed-in person's list with only their bars.
  dayMap,
}) {
  // {storeTeamMemberId, clockPin, name} while someone is signed in.
  const [identity, setIdentity] = React.useState(null);
  const [nowData, setNowData] = React.useState(null);
  const [nowError, setNowError] = React.useState(false);
  const [showDone, setShowDone] = React.useState(false);
  const [showUpcoming, setShowUpcoming] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [roster, setRoster] = React.useState(null);
  // A failed roster fetch is its own state: an empty roster would tell the
  // person nobody works here, and a full reload of the kiosk means a
  // manager re-login.
  const [rosterError, setRosterError] = React.useState(false);
  const [workRow, setWorkRow] = React.useState(null);
  // Which tab the modal opens on: the row's comment icon asks for the thread.
  const [workTab, setWorkTab] = React.useState("checklist");
  // Who was signed in when an async step began, so a sign-out mid-fetch
  // never opens the previous person's checklist for the next one.
  const identityRef = React.useRef(null);
  identityRef.current = identity;

  const identityParams = React.useCallback(
    (who) => (who?.clockPin ? { clock_pin: who.clockPin } : { store_team_member_id: who?.storeTeamMemberId }),
    []
  );

  // List mode needs the roster; PIN mode deliberately never loads it. A
  // response for a previous store is dropped.
  const rosterStoreRef = React.useRef(null);
  const loadRoster = React.useCallback(async () => {
    if (pinRequired || !storeId) return;
    rosterStoreRef.current = storeId;
    setRosterError(false);
    try {
      const axiosConfig = await getDefaultAxiosConfig();
      const res = await axios.get(`${API_get_store_team_members}${storeId}`, axiosConfig);
      if (rosterStoreRef.current !== storeId) return;
      setRoster(
        (res.data || []).filter(
          (m) => !m.is_archived && m.permission_type !== "company_timeclock"
        )
      );
    } catch (error) {
      console.error("KioskTasksPanel: roster fetch failed", error);
      if (rosterStoreRef.current === storeId) {
        setRoster(null);
        setRosterError(true);
      }
    }
  }, [storeId, pinRequired]);
  React.useEffect(() => {
    setRoster(null);
    loadRoster();
  }, [loadRoster]);

  // The person's Now list. A response for someone who has since signed out
  // (or for a previous store) is dropped.
  const contextRef = React.useRef(null);
  // Done and upcoming rows always come along: the table shows them only on
  // request, but the person's day strip needs every bar that is theirs.
  const fetchNow = React.useCallback(
    async (who) => {
      const context = `${storeId}:${who?.storeTeamMemberId ?? who?.clockPin}`;
      contextRef.current = context;
      const res = await getRightworkAPI(API_get_now_tasks_kiosk, {
        store_id: storeId,
        include_done: "true",
        include_upcoming: "true",
        ...identityParams(who),
      });
      if (contextRef.current !== context) return null;
      return res.data?.redux?.payload || null;
    },
    [storeId, identityParams]
  );

  const signOut = React.useCallback(() => {
    contextRef.current = null;
    setIdentity(null);
    setNowData(null);
    setNowError(false);
    setWorkRow(null);
    setShowDone(false);
  }, []);

  // Identify: the kiosk Now fetch IS the identity check (the server resolves
  // the person and refuses an unknown pin or id), so one round trip does both.
  const identify = async (params) => {
    if (busy) return null;
    setBusy(true);
    const who = params.clock_pin
      ? { clockPin: params.clock_pin }
      : { storeTeamMemberId: params.store_team_member_id };
    try {
      const payload = await fetchNow(who);
      if (!payload) return "Could not load your tasks. Try again.";
      setIdentity({
        ...who,
        storeTeamMemberId: payload.kiosk_member?.store_team_member_id ?? who.storeTeamMemberId,
        name: payload.kiosk_member?.full_name || "",
        initials: payload.kiosk_member?.initials || "",
      });
      setNowData(payload);
      setNowError(false);
      return null;
    } catch (error) {
      return errorMessage(error, "Could not load your tasks. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // Resolves false when the Now list could not be refetched.
  const refresh = React.useCallback(async () => {
    if (!identity) return false;
    try {
      const payload = await fetchNow(identity);
      if (payload) {
        setNowData(payload);
        setNowError(false);
      }
      return !!payload;
    } catch (error) {
      console.error("KioskTasksPanel: refresh failed", error);
      setNowError(true);
      return false;
    }
  }, [identity, fetchNow]);

  // Refetch once a minute while signed in.
  React.useEffect(() => {
    if (!identity) return undefined;
    refresh();
    const interval = setInterval(refresh, POLL_MS);
    return () => clearInterval(interval);
  }, [identity, refresh]);

  // Idle sign-out. Any touch, click or key on the page (the modal renders in
  // a portal, so listen on the document) restarts the timer.
  React.useEffect(() => {
    if (!identity) return undefined;
    let timer = setTimeout(signOut, KIOSK_IDLE_MS);
    const bump = () => {
      clearTimeout(timer);
      timer = setTimeout(signOut, KIOSK_IDLE_MS);
    };
    const events = ["pointerdown", "keydown", "touchstart", "wheel"];
    events.forEach((e) => document.addEventListener(e, bump, { passive: true }));
    return () => {
      clearTimeout(timer);
      events.forEach((e) => document.removeEventListener(e, bump));
    };
  }, [identity, signOut]);

  // Signing out while the store changes under us: a new store is a new visit.
  React.useEffect(() => {
    signOut();
  }, [storeId, signOut]);

  // Back to the top whenever the person changes. The roster runs long, so a
  // name picked from its bottom would otherwise open that person's list
  // scrolled down, and the next person would meet the gate the same way. The
  // page scrolls inside its own container, not the window, so find whichever
  // ancestor actually scrolls.
  const rootRef = React.useRef(null);
  React.useEffect(() => {
    let node = rootRef.current?.parentElement;
    while (node) {
      const { overflowY } = window.getComputedStyle(node);
      if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
        node.scrollTop = 0;
        return;
      }
      node = node.parentElement;
    }
    window.scrollTo(0, 0);
  }, [identity]);

  // The checklist needs the day-payload occurrence (steps and answers); the
  // Now row supplies the person's lifecycle and clock. Today's payload is
  // already here; a carried routine or an old one-off is fetched for its date.
  const occurrenceFor = (row) => {
    for (const task of planTasks || []) {
      const occ = (task.plan_task_assignments || []).find((a) => a.uuid === row.plan_task_assignment_uuid);
      if (occ) return { ...occ, plan_task: occ.plan_task || task };
    }
    return null;
  };
  const fetchOccurrence = async (row) => {
    const axiosConfig = await getDefaultAxiosConfig();
    const res = await axios.get(
      `${API_get_plan_tasks_for_date}?store_id=${storeId}&date=${row.date}`,
      axiosConfig
    );
    for (const task of res.data?.redux?.payload?.plan_tasks || []) {
      const occ = (task.plan_task_assignments || []).find((a) => a.uuid === row.plan_task_assignment_uuid);
      if (occ) return { ...occ, plan_task: occ.plan_task || task };
    }
    return null;
  };
  const openWork = async (row, intent) => {
    const who = identityRef.current;
    try {
      let occ = occurrenceFor(row);
      if (!occ) occ = await fetchOccurrence(row);
      if (identityRef.current !== who) return; // signed out meanwhile
      if (!occ) {
        toast.error("That task could not be loaded. Refresh and try again.", { autoClose: 3000 });
        return;
      }
      setWorkTab(intent === "conversation" ? "comments" : "checklist");
      setWorkRow(mergeNowRowOverOccurrence(row, { ...occ, store_id: occ.store_id ?? storeId }));
    } catch (error) {
      toast.error("That task could not be loaded. Refresh and try again.", { autoClose: 3000 });
    }
  };

  // Waits for the parent's day payload too: a checklist reopens from it
  // (occurrenceFor), so closing before it lands would show the old answers.
  const afterChange = async () => {
    const refreshed = await refresh();
    // A parent that reports nothing (undefined) is not a failure.
    const parentRefreshed = (await onChanged?.()) !== false;
    return refreshed && parentRefreshed;
  };

  // Save / submit through the kiosk twin, as the signed-in person.
  // Resolves true once saved, false on failure (the modal stays open).
  const save_plan_task_assignment = async (plan_task_assignment_uuid, plan_subtask_assignments, is_submit) => {
    if (!identity) return false;
    try {
      toast.loading(is_submit ? "Submitting..." : "Saving...", { autoClose: false });
      const axiosConfig = await getDefaultAxiosConfig();
      const res = await axios.post(
        API_save_plan_task_assignment_kiosk,
        {
          store_id: storeId,
          ...identityParams(identity),
          plan_task_assignment_uuid,
          // Answers go up as they are, null included: the server reads a
          // null-to-false change as work done (it stamps started_at, claims
          // the task and opens a session), so an untouched checklist saved
          // for progress must stay untouched, as the employee save keeps it.
          plan_subtask_assignments: JSON.stringify(
            (plan_subtask_assignments || []).map((s) => ({
              uuid: s.uuid,
              value_text: s.value_text ?? null,
              value_boolean: s.value_boolean ?? null,
              // Edit pressed on a finished shared step: let the change in.
              ...(s.reopened ? { reopened: true } : {}),
              // photo_uuids once photos were added or removed here; an
              // untouched step re-sends its first photo, which changes nothing.
              ...(s.photo_uuids
                ? { photo_uuids: s.photo_uuids }
                : { file_upload: s.file_upload?.uuid ? { uuid: s.file_upload.uuid } : null }),
            }))
          ),
          is_submit: !!is_submit,
        },
        axiosConfig
      );
      toast.dismiss();
      if (res.status === 200) {
        if (!(await afterChange())) {
          toast.error("Saved, but the task list could not refresh. Check your connection.", { autoClose: 4000 });
          return false;
        }
        toast.success(is_submit ? "Submitted" : "Saved", { autoClose: 2500 });
        return true;
      }
    } catch (error) {
      toast.dismiss();
      toast.error(errorMessage(error, "Could not save the task."), { autoClose: 3000 });
    }
    return false;
  };

  // The person's bars, and the rows a bar can open: what can be done now and
  // what is done today, the same rows the table opens. Upcoming rows are
  // display only in the table too.
  const myEntries = React.useMemo(
    () => entriesForPerson(dayMap?.entries, nowData),
    [dayMap, nowData]
  );
  const openableRowByUuid = React.useMemo(() => {
    const byUuid = {};
    [...(nowData?.tasks || []), ...(nowData?.done_today || [])].forEach((row) => {
      byUuid[row.plan_task_assignment_uuid] = row;
    });
    return byUuid;
  }, [nowData]);

  if (!identity) {
    return (
      <div ref={rootRef}>
        <KioskIdentityGate
          pinRequired={!!pinRequired}
          roster={roster}
          rosterError={rosterError}
          onRetryRoster={loadRoster}
          onIdentify={identify}
          busy={busy || (!pinRequired && roster === null && !rosterError)}
        />
      </div>
    );
  }

  return (
    <div ref={rootRef}>
      {/* Who is signed in, and the one button to press when finished. It
          stays pinned while the list scrolls, and it is loud on purpose: the
          alternative people reach for is the Exit button up top, which logs
          the whole kiosk out. */}
      <div className="sticky top-0 z-30 bg-white py-2 mb-2">
        <div className="flex items-center justify-between gap-4 rounded-xl border border-violet-200 bg-violet-50 px-5 py-4 shadow-sm">
          <div className="flex items-center gap-4 min-w-0">
            <span className="h-12 w-12 flex-none rounded-full bg-violet-700 text-white text-base font-bold flex items-center justify-center">
              {identity.initials || "?"}
            </span>
            <div className="min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-wider text-violet-700">
                Signed in as
              </div>
              <div className="text-2xl font-bold text-slate-900 leading-tight truncate">
                {identity.name || "You"}
              </div>
              <div className="text-sm text-slate-600 mt-0.5">
                Tap Done when you finish so the next person can sign in.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={signOut}
            className="flex-none inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-violet-700 hover:bg-violet-900 active:bg-violet-950 text-white text-lg font-semibold shadow"
          >
            <LogoutIcon sx={{ fontSize: 22 }} />
            Done, sign out
          </button>
        </div>
      </div>

      {nowError && !nowData ? (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <p className="text-sm text-slate-500">Your tasks could not be loaded.</p>
          <button onClick={refresh} className="text-sm font-semibold text-violet-700 hover:text-violet-800">
            Try again
          </button>
        </div>
      ) : nowData ? (
        <NowTable
          data={nowData}
          storeId={storeId}
          showDone={showDone}
          onToggleDone={setShowDone}
          showUpcoming={showUpcoming}
          onToggleUpcoming={setShowUpcoming}
          onOpenTask={openWork}
          showHistory={false}
        />
      ) : (
        <div className="flex justify-center py-16">
          <LoadSpinner />
        </div>
      )}

      {nowData && dayMap && myEntries.length > 0 && (
        <div className="mt-6">
          <div className="text-sm font-semibold text-slate-900 mb-2 px-1">Your day</div>
          <DayGanttMap
            entries={myEntries}
            axisStart={dayMap.axisStart}
            axisEnd={dayMap.axisEnd}
            nowQh={dayMap.nowQh}
            onBarClick={(entry) => {
              const row = openableRowByUuid[entry.key];
              if (row) openWork(row);
            }}
          />
        </div>
      )}

      {workRow && (
        <TaskNameCardEmployeeModal
          open={!!workRow}
          handleClose={() => setWorkRow(null)}
          task={workRow}
          initialTab={workTab}
          kiosk={{ storeTeamMemberId: identity.storeTeamMemberId, clockPin: identity.clockPin || null }}
          onCommented={afterChange}
          isAssignedToEveryone={!workRow.is_pinned}
          save_plan_task_assignment={save_plan_task_assignment}
          homeCompany={homeCompany}
          roleNames={roleNames}
          setShowLightbox={() => {}}
          setSelectedImage={() => {}}
        />
      )}
    </div>
  );
}
